import { html } from 'lit';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type GanttScale = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';

export const GANTT_SCALES: {
  value: GanttScale;
  label: string;
  short: string;
  /** nominal milliseconds one column covers (uneven scales use calendar walk) */
  step: number;
  /** column width in px */
  width: number;
}[] = [
  { value: 'hour', label: 'Часы', short: 'Ч', step: HOUR, width: 60 },
  { value: 'day', label: 'Дни', short: 'Д', step: DAY, width: 28 },
  { value: 'week', label: 'Недели', short: 'Н', step: 7 * DAY, width: 56 },
  { value: 'month', label: 'Месяцы', short: 'М', step: 28 * DAY, width: 80 },
  {
    value: 'quarter',
    label: 'Кварталы',
    short: 'К',
    step: 62 * DAY,
    width: 100,
  },
  { value: 'year', label: 'Годы', short: 'Г', step: 365 * DAY, width: 120 },
];

export const scaleSpec = (scale: GanttScale) =>
  GANTT_SCALES.find(s => s.value === scale) ?? GANTT_SCALES[1]!;

/** Snap a timestamp to the start of its column (local time). */
export const snapToColumn = (time: number, scale: GanttScale): number => {
  const date = new Date(time);
  switch (scale) {
    case 'hour':
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours()
      ).getTime();
    case 'week': {
      const day = date.getDay();
      const monday = new Date(date);
      monday.setDate(date.getDate() - ((day + 6) % 7));
      monday.setHours(0, 0, 0, 0);
      return monday.getTime();
    }
    case 'month':
      return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
    case 'quarter':
      return new Date(
        date.getFullYear(),
        Math.floor(date.getMonth() / 3) * 3,
        1
      ).getTime();
    case 'year':
      return new Date(date.getFullYear(), 0, 1).getTime();
    default:
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).getTime();
  }
};

/**
 * Start of the column `index` steps after `origin` (`origin` is snapped to a
 * column start). All scales are walked with calendar arithmetic — the fixed
 * `step` values are shorter than real months/quarters/years and would snap
 * back onto the same column («stuck months»).
 */
export const columnTime = (
  index: number,
  origin: number,
  scale: GanttScale
): number => {
  const base = new Date(snapToColumn(origin, scale));
  switch (scale) {
    case 'hour':
      return new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
        base.getHours() + index
      ).getTime();
    case 'day':
      return new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate() + index
      ).getTime();
    case 'week':
      return new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate() + 7 * index
      ).getTime();
    case 'month':
      return new Date(base.getFullYear(), base.getMonth() + index, 1).getTime();
    case 'quarter':
      return new Date(
        base.getFullYear(),
        Math.floor(base.getMonth() / 3) * 3 + 3 * index,
        1
      ).getTime();
    case 'year':
      return new Date(base.getFullYear() + index, 0, 1).getTime();
  }
};

/**
 * Index of the column containing `time`, relative to the `origin` column.
 * Exact for month/quarter/year (calendar diff), and a corrected linear
 * estimate for hour/day/week (DST days are 23/25 hours long).
 */
export const columnIndexOf = (
  time: number,
  origin: number,
  scale: GanttScale
): number => {
  const o = new Date(snapToColumn(origin, scale));
  const d = new Date(time);
  switch (scale) {
    case 'month':
      return (
        (d.getFullYear() - o.getFullYear()) * 12 + (d.getMonth() - o.getMonth())
      );
    case 'quarter':
      return (
        (d.getFullYear() - o.getFullYear()) * 4 +
        (Math.floor(d.getMonth() / 3) - Math.floor(o.getMonth() / 3))
      );
    case 'year':
      return d.getFullYear() - o.getFullYear();
    default: {
      const spec = scaleSpec(scale);
      const oTime = o.getTime();
      let index = Math.floor((time - oTime) / spec.step);
      // correct DST drift: columns advance by calendar units, not fixed ms
      for (let k = 0; k < 8 && columnTime(index, oTime, scale) > time; k++) {
        index -= 1;
      }
      for (
        let k = 0;
        k < 8 && columnTime(index + 1, oTime, scale) <= time;
        k++
      ) {
        index += 1;
      }
      return index;
    }
  }
};

/** Full column descriptor at `index` (absolute x within the chart). */
export const columnAt = (
  index: number,
  origin: number,
  scale: GanttScale
): { label: string; x: number; width: number; weekend: boolean } => {
  const spec = scaleSpec(scale);
  const start = columnTime(index, origin, scale);
  const next = columnTime(index + 1, origin, scale);
  const date = new Date(start);
  return {
    label: columnLabel(start, next, scale),
    x: index * spec.width,
    width: spec.width,
    weekend:
      scale === 'hour'
        ? false
        : scale === 'day'
          ? [0, 6].includes(date.getDay())
          : false,
  };
};

/**
 * First column start for the chart; one column before the data so the first
 * bar is not glued to the task column.
 */
export const chartStart = (rangeStart: number, scale: GanttScale) =>
  columnTime(-1, rangeStart, scale);

/** Offset (px) of a time inside the chart for the given scale. */
export const timeToX = (
  time: number,
  rangeStart: number,
  scale: GanttScale
): number => {
  const spec = scaleSpec(scale);
  const origin = snapToColumn(rangeStart, scale);
  const index = columnIndexOf(time, origin, scale);
  const colStart = columnTime(index, origin, scale);
  const colEnd = columnTime(index + 1, origin, scale);
  const fraction = (time - colStart) / (colEnd - colStart);
  return (index + fraction) * spec.width;
};

/** Column headers (label + x offset + width) covering [rangeStart, rangeEnd]. */
export const buildColumns = (
  rangeStart: number,
  rangeEnd: number,
  scale: GanttScale
): { label: string; x: number; width: number; weekend: boolean }[] => {
  const origin = snapToColumn(rangeStart, scale);
  const lastIndex = columnIndexOf(rangeEnd, origin, scale);
  const columns: {
    label: string;
    x: number;
    width: number;
    weekend: boolean;
  }[] = [];
  for (let i = 0; i <= lastIndex; i++) {
    columns.push(columnAt(i, origin, scale));
  }
  return columns;
};

const columnLabel = (
  start: number,
  _next: number,
  scale: GanttScale
): string => {
  const date = new Date(start);
  switch (scale) {
    case 'hour':
      return `${String(date.getHours()).padStart(2, '0')}:00`;
    case 'day':
      // 28 px columns: only the day number fits, the month lives in the band
      // above the day row
      return String(date.getDate());
    case 'week':
      return date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
      });
    case 'month':
      return date.toLocaleDateString(undefined, {
        month: 'short',
        year: '2-digit',
      });
    case 'quarter':
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${String(date.getFullYear()).slice(2)}`;
    case 'year':
      return String(date.getFullYear());
    default:
      return '';
  }
};

/** Month captions drawn above the day/week columns. */
export const buildMonthBands = (
  from: number,
  to: number,
  scale: GanttScale
): { label: string; x: number; width: number }[] => {
  if (scale !== 'day' && scale !== 'week') return [];
  const bands: { label: string; x: number; width: number }[] = [];
  const cursor = new Date(from);
  let month = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  let guard = 0;
  while (month.getTime() <= to && guard < 240) {
    const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    const rawX = timeToX(month.getTime(), from, scale);
    const rawEnd = timeToX(next.getTime(), from, scale);
    const x = Math.max(0, rawX);
    const width = rawEnd - x;
    if (width > 0) {
      bands.push({
        label: month.toLocaleDateString(undefined, {
          month: 'short',
          year: 'numeric',
        }),
        x,
        width,
      });
    }
    month = next;
    guard += 1;
  }
  return bands;
};

/** Compact scale picker: one dropdown instead of six buttons. */
export const renderScaleSwitcher = (
  current: GanttScale,
  onChange: (scale: GanttScale) => void
) => {
  const active = GANTT_SCALES.find(option => option.value === current);
  return html`<span style="position:relative">
    <details class="orghub-gantt-scale">
      <summary
        style="padding:3px 10px;border-radius:6px;border:1px solid var(--affine-border-color);cursor:pointer;list-style:none;display:flex;align-items:center;gap:6px;user-select:none"
      >
        <span>${active?.label ?? 'Дни'}</span>
        <span style="opacity:.6;font-size:10px">▾</span>
      </summary>
      <div
        style="position:absolute;right:0;top:calc(100% + 4px);z-index:10;min-width:150px;padding:4px;border-radius:8px;border:1px solid var(--affine-border-color);background:var(--affine-background-primary-color);box-shadow:0 8px 24px rgba(0,0,0,.2);display:flex;flex-direction:column"
      >
        ${GANTT_SCALES.map(
          option =>
            html`<button
              data-scale=${option.value}
              @click=${(event: Event) => {
                const details = (event.currentTarget as HTMLElement).closest(
                  'details'
                );
                if (details) details.open = false;
                onChange(option.value);
              }}
              style="text-align:left;padding:6px 8px;border:0;border-radius:6px;background:${current ===
              option.value
                ? 'var(--affine-hover-color)'
                : 'transparent'};color:var(--affine-text-primary-color);cursor:pointer;font-size:12px"
            >
              ${option.label}
            </button>`
        )}
      </div>
    </details>
  </span>`;
};
